import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Factory, Lightning, Clock, ArrowsCounterClockwise, Warning, Pause } from "@phosphor-icons/react";
import { productionStageLabel } from "@/lib/caseStatus";

/**
 * ProductionKpisWidget — admin Phase-2 health snapshot. Pulls
 * /api/admin/production-kpis and renders 4 KPI tiles + a stage breakdown
 * bar. Designed to drop above the existing CancellationReasonsWidget on
 * the /admin/cases page.
 */
export function ProductionKpisWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get("/admin/production-kpis")
            .then((r) => { if (alive) setData(r.data); })
            .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Failed to load KPIs"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground" data-testid="prod-kpis-loading">
                Loading production KPIs…
            </div>
        );
    }
    if (err || !data) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="prod-kpis-error">
                {err || "Couldn't load production KPIs"}
            </div>
        );
    }

    const stages = data.active_cases_by_stage || {};
    const totalActive = Object.values(stages).reduce((s, n) => s + (n || 0), 0);

    return (
        <div
            className="rounded-2xl border border-border bg-card p-5 sm:p-6 space-y-5"
            data-testid="prod-kpis-widget"
        >
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                        Production health
                    </div>
                    <h2 className="font-display text-lg font-bold tracking-tight mt-0.5">
                        Phase 2 dashboard
                    </h2>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Last {data.window_days?.main || 90}d · shipped: {data.window_days?.shipped || 30}d
                </div>
            </div>

            {/* KPI tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Kpi
                    Icon={Lightning}
                    tone="emerald"
                    label="QC pass first try"
                    value={`${data.qc_pass_first_attempt_pct}%`}
                    hint={`${data.qc_total_reviewed_90d} cases reviewed`}
                    testId="kpi-qc-pass"
                />
                <Kpi
                    Icon={ArrowsCounterClockwise}
                    tone="amber"
                    label="Avg rework cycles"
                    value={data.avg_rework_cycles_per_case}
                    hint="per case"
                    testId="kpi-rework"
                />
                <Kpi
                    Icon={Clock}
                    tone="violet"
                    label="Prep → Shipped"
                    value={`${data.days_digital_prep_to_shipped?.median ?? 0}d`}
                    hint={`p90: ${data.days_digital_prep_to_shipped?.p90 ?? 0}d · n=${data.days_digital_prep_to_shipped?.sample_size ?? 0}`}
                    testId="kpi-leadtime"
                />
                <Kpi
                    Icon={Factory}
                    tone="primary"
                    label="Shipped (30d)"
                    value={data.cases_shipped_30d}
                    hint="cases delivered"
                    testId="kpi-shipped"
                />
            </div>

            {/* Stage breakdown */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                        Active cases by stage
                    </div>
                    <div className="text-xs font-semibold tabular-nums">
                        {totalActive} in production
                    </div>
                </div>
                <div className="rounded-xl border border-border/60 bg-secondary/30 p-3">
                    {totalActive === 0 ? (
                        <div className="text-xs text-muted-foreground italic">No cases currently in manufacturing.</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                            {Object.entries(stages)
                                .filter(([, n]) => n > 0)
                                .map(([stage, n]) => (
                                    <div
                                        key={stage}
                                        className="rounded-lg bg-card border border-border/60 p-2 flex items-center justify-between"
                                        data-testid={`prod-kpis-stage-${stage}`}
                                    >
                                        <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider truncate">
                                            {productionStageLabel(stage)}
                                        </span>
                                        <span className="text-sm font-display font-bold tabular-nums text-violet-700 ml-2 shrink-0">
                                            {n}
                                        </span>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Holds + escalations */}
            {(data.cases_on_hold?.total > 0 || data.escalations_open > 0) && (
                <div className="flex flex-wrap gap-2 text-xs">
                    {data.cases_on_hold?.total > 0 && (
                        <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-semibold"
                            data-testid="prod-kpis-on-hold"
                        >
                            <Pause size={12} weight="fill" />
                            {data.cases_on_hold.total} on hold
                            {Object.keys(data.cases_on_hold.by_kind || {}).length > 0 && (
                                <span className="text-amber-700/80 font-normal ml-1">
                                    ({Object.entries(data.cases_on_hold.by_kind).map(([k, v]) => `${v} ${k}`).join(", ")})
                                </span>
                            )}
                        </span>
                    )}
                    {data.escalations_open > 0 && (
                        <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-900 font-semibold"
                            data-testid="prod-kpis-escalations"
                        >
                            <Warning size={12} weight="fill" />
                            {data.escalations_open} escalation{data.escalations_open === 1 ? "" : "s"} open
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

function Kpi({ Icon, tone, label, value, hint, testId }) {
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : "text-primary bg-primary/10";
    return (
        <div
            className="rounded-xl border border-border/60 bg-secondary/30 p-3 flex items-center gap-3"
            data-testid={testId}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0">
                <div className="text-xl font-display font-bold tabular-nums leading-none">{value}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5 truncate">{label}</div>
                {hint && <div className="text-[9px] text-muted-foreground/80 mt-0.5 truncate">{hint}</div>}
            </div>
        </div>
    );
}

export default ProductionKpisWidget;
